import React, { type FC } from 'react'
import {
  Table,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Checkbox,
  TableBody,
  Box
} from '@mui/material'
import EmptyTable from './EmptyTable'
import { alarmMetaData } from '../../redux/actionCreators'
import { type AlarmMetaDataState } from '../../redux/alarmMetaDataSlice'
import { type RootState } from '../../../../store'
import { connect, type ConnectedProps, useSelector } from 'react-redux'
import ConfigureDevice from '../configureDevice'
import { type Parameter } from '../interface'
import { GenXDropdown, type Option } from '../../../../components/GenXDropdown'

const styles = {
  selectAllCheckbox: {
    color: '#909090'
  },
  checkbox: {
    color: '#909090'
  },
  table: {
    backgroundColor: '#272727',
    borderBottom: 'none',
    boxShadow: 'none !important'
  },
  tableHead: {
    backgroundColor: '#303030',
    border: '1px solid #101010',
    boxShadow: '1px 1px 1px 0px rgba(0, 0, 0, 0.15)',
    borderRadius: '10px 10px 0px 0px'
  },
  tableHeadCell: {
    backgroundColor: '#303030',
    borderBottom: 'none',
    padding: '5px'
  },
  tableCell: {
    backgroundColor: '#272727',
    borderBottom: 'none',
    padding: '5px'
  },
  tableHeaderTypography: {
    color: '#F0F0F0',
    fontFeatureSettings: "'clig' off, 'liga' off",
    fontFamily: 'Honeywell Sans Web',
    fontSize: '16px',
    fontStyle: 'normal',
    fontWeight: 800,
    lineHeight: '24px'
  },
  tableCellTypography: {
    color: '#F0F0F0',
    fontFeatureSettings: "'clig' off, 'liga' off",
    fontFamily: 'Honeywell Sans Web',
    fontSize: '16px',
    fontStyle: 'normal',
    fontWeight: 500,
    lineHeight: '24px'
  },
  tableContainer: {
    height: '430px',
    overflow: 'auto',
    '&::-webkit-scrollbar': { width: '12px' },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: '#FFFFFF80'
    },
    '&::-webkit-scrollbar-thumb:hover': {
      backgroundColor: '#FFFFFF80'
    }
  }
}

/**
 * Helper function to convert frequency options to display value options
 * @param frequencyOptions - API frequency options array
 * @returns Array of options formatted for GenXDropdown component
 */
const convertSecondsToDisplayValueOption = (frequencyOptions: Array<{ label: string, value: string }>): Option[] => {
  return frequencyOptions.map(option => ({
    label: option.label,
    value: option.value
  }))
}
interface ParametersTableProps {
  tableHeaders: string[]
  parameters: Parameter[] | []
  searchText: string
  selectedModbusCount: number
  toggleSelectAll: () => void
  toggleSelectIndicator: (id: number) => void
  handleChangeTextBox: (
    registerAddress: number | string,
    field: string,
    value: string | number
  ) => void
  handleAlarmSelect: (
    registerAddress: number | string,
    field: string,
    value: string
  ) => void
  handleCancel: (id: string | number) => void
}

const ParametersTable: FC<ParametersTableProps & PropsFromRedux> = ({
  searchText,
  tableHeaders,
  parameters,
  selectedModbusCount,
  toggleSelectAll,
  handleAlarmSelect,
  toggleSelectIndicator,
  handleChangeTextBox,
  alarmMetaDataState,
  handleCancel
}) => {
  const frequencyMetaDataState = useSelector((state: RootState) => state.frequencyMetaDataState)

  const executionFrequencyOptions = React.useMemo(() => {
    if (frequencyMetaDataState.data?.modbusFrequencies != null && frequencyMetaDataState.data.modbusFrequencies.length > 0) {
      return convertSecondsToDisplayValueOption(frequencyMetaDataState.data.modbusFrequencies)
    }
    // Fallback to static options if API data is not available
    return [
      { label: '10 Seconds', value: '10' },
      { label: '30 Seconds', value: '30' },
      { label: 'Default (1 Minute)', value: '60' }
    ]
  }, [frequencyMetaDataState.data?.modbusFrequencies])

  React.useEffect(() => {
    alarmMetaData()
  }, [])

  const filteredParameters =
    searchText !== ''
      ? parameters.filter(
        (parameter) =>
          parameter.parameterName
            ?.toLowerCase()
            .includes(searchText.toLowerCase()) ||
            parameter.registerAddress?.toString().includes(searchText)
      )
      : parameters

  return (
    <TableContainer className='modbus-table' sx={styles.tableContainer}>
      <Table sx={styles.table}>
        <TableHead sx={styles.tableHead}>
          <TableRow>
            <TableCell sx={styles.tableHeadCell}>
              <Checkbox
                sx={styles.selectAllCheckbox}
                data-testid='select-all-checkbox'
                className='select-all-checkbox'
                indeterminate={
                  selectedModbusCount > 0 &&
                  selectedModbusCount < filteredParameters.length
                }
                checked={
                  filteredParameters.length > 0 &&
                  selectedModbusCount === filteredParameters.length
                }
                onChange={toggleSelectAll}
                inputProps={{
                  'aria-label': 'Select all'
                }}
              />
            </TableCell>
            {tableHeaders.map((header) => (
              <TableCell key={header} sx={styles.tableHeadCell}>
                <Typography sx={styles.tableHeaderTypography}>
                  {header}
                </Typography>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        {filteredParameters.length === 0
          ? (
          <EmptyTable />
            )
          : (
          <TableBody className='modbus-table-body'>
            {filteredParameters.map((indicator, index) => (
              <TableRow key={indicator.registerAddress}>
                <TableCell sx={styles.tableCell}>
                  <Checkbox
                    color='primary'
                    checked={indicator.isRegisterEnabled}
                    onChange={() => {
                      toggleSelectIndicator(indicator.registerAddress)
                    }}
                    data-testid={`select-indicator-${indicator.registerAddress}`}
                    sx={styles.checkbox}
                  />
                </TableCell>
                <TableCell sx={styles.tableCell}>
                  <Typography sx={styles.tableCellTypography}>
                    {indicator.registerAddress}
                  </Typography>
                </TableCell>
                <TableCell sx={styles.tableCell}>
                  <Typography sx={styles.tableCellTypography}>
                    {indicator.parameterName}
                  </Typography>
                </TableCell>
                <TableCell sx={styles.tableCell}>
                  <Typography sx={styles.tableCellTypography}>
                    <Box
                      mb={2}
                      sx={{
                        marginTop: '8px',
                        width: '198px',
                        height: '32px',
                        position: 'relative',
                        zIndex: 100 - index
                      }}
                    >
                      <GenXDropdown
                        options={executionFrequencyOptions}
                        value={executionFrequencyOptions.find(option => option.value === String(indicator.pollingIntervalMinutes ?? 60)) ?? null}
                        onSelect={(selectedOption: Option) => {
                          handleChangeTextBox(
                            indicator.registerAddress,
                            'pollingIntervalMinutes',
                            parseInt(selectedOption.value)
                          )
                        }}
                        placeholder="Select Frequency"
                        width={198}
                        height={150}
                        disabled={!indicator.isRegisterEnabled}
                        inputId={`pooling-interval-${indicator.registerAddress}`}
                        isSearchable={false}
                      />
                    </Box>
                  </Typography>
                </TableCell>
                <TableCell sx={styles.tableCell}>
                       <ConfigureDevice
                    isEdit={false}
                    isAlarmEnabledCompo={!indicator.isRegisterEnabled}
                    deviceIndicators={indicator}
                    handleChangeTextBox={handleChangeTextBox}
                    alarmSelect={handleAlarmSelect}
                    handleCancel={handleCancel}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
            )}
      </Table>
    </TableContainer>
  )
}

interface DispatchToProps {
  alarmMetaData: () => void
}
const mapDispatchToProps = (dispatch: any): DispatchToProps => ({
  alarmMetaData: () => dispatch(alarmMetaData())
})
interface StateToProps {
  alarmMetaDataState: AlarmMetaDataState
}
const mapStateToProps = (state: RootState): StateToProps => ({
  alarmMetaDataState: state.alarmMetaDataState
})

const connector = connect(mapStateToProps, mapDispatchToProps)
type PropsFromRedux = ConnectedProps<typeof connector>

export default connector(ParametersTable)
